/* Prompt 09 — Explainable child timeline page.

   Toont per kind alle events (aanmelding/contract/aanwezigheid/communicatie/
   incident/medicatie/factuur/audit) chronologisch. Backend RBAC bepaalt wat
   zichtbaar is per rol.
*/
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FileText, ClipboardCheck, CalendarCheck, MessageCircle, AlertTriangle,
  Pill, FileCheck2, ShieldAlert, ArrowLeft,
} from "lucide-react";

const TYPE_META = {
  application:   { label: "Aanmelding",  Icon: FileText,       hue: "bg-slate-100 text-slate-800 border-slate-200" },
  contract:      { label: "Contract",    Icon: ClipboardCheck, hue: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  attendance:    { label: "Aanwezigheid",Icon: CalendarCheck,  hue: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  communication: { label: "Bericht",     Icon: MessageCircle,  hue: "bg-blue-100 text-blue-800 border-blue-200" },
  incident:      { label: "Incident",    Icon: AlertTriangle,  hue: "bg-amber-100 text-amber-800 border-amber-200" },
  medication:    { label: "Medicatie",   Icon: Pill,           hue: "bg-pink-100 text-pink-800 border-pink-200" },
  invoice:       { label: "Factuur",     Icon: FileCheck2,     hue: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  audit:         { label: "Audit",       Icon: ShieldAlert,    hue: "bg-neutral-200 text-neutral-800 border-neutral-300" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function KindTimelinePage() {
  const { kindId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [activeTypes, setActiveTypes] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErr(null);
    const params = {};
    if (activeTypes && activeTypes.length > 0) {
      params.types = activeTypes.join(",");
    }
    api.get(`/kinderen/${kindId}/timeline`, { params })
      .then(r => { if (!cancel) setData(r.data); })
      .catch(e => {
        if (cancel) return;
        setErr(e?.response?.status === 403
          ? "Geen toegang tot dit kind"
          : e?.response?.status === 404
          ? "Kind niet gevonden"
          : "Kon tijdlijn niet laden");
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
    // `api` is stabiele module-level import.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindId, activeTypes]);

  if (loading && !data) {
    return <div className="p-8 text-center text-neutral-500" data-testid="timeline-loading">Tijdlijn laden…</div>;
  }
  if (err) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="timeline-error">
        <Card className="p-6 border-red-200 bg-red-50/50">
          <div className="text-red-800 font-semibold">{err}</div>
        </Card>
      </div>
    );
  }
  if (!data) return null;

  const toggleType = (t) => {
    if (!activeTypes) return setActiveTypes([t]);
    const has = activeTypes.includes(t);
    const next = has ? activeTypes.filter(x => x !== t) : [...activeTypes, t];
    setActiveTypes(next.length === data.available_types.length ? null : next);
  };
  const clearFilter = () => setActiveTypes(null);

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="kind-timeline-page">
      <div className="mb-4 flex items-center gap-3">
        <Link to="/app/kinderen" className="text-neutral-600 hover:text-neutral-900"
              data-testid="timeline-back">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900" data-testid="timeline-kind-naam">
            {data.kind.voornaam} {data.kind.achternaam}
          </h1>
          <div className="text-xs text-neutral-500">
            {data.kind.geboortedatum && <>Geboren {data.kind.geboortedatum} · </>}
            {data.count} events · gegenereerd {fmtDate(data.generated_at)}
          </div>
        </div>
      </div>

      {/* Type-filter chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="timeline-filter">
        <button
          onClick={clearFilter}
          data-testid="timeline-filter-all"
          className={`text-xs px-2.5 py-1 rounded-full border transition
            ${!activeTypes ? "bg-neutral-900 text-white border-neutral-900"
                            : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"}`}
        >Alles</button>
        {data.available_types.map(t => {
          const meta = TYPE_META[t] || {};
          const on = activeTypes ? activeTypes.includes(t) : true;
          return (
            <button
              key={t}
              onClick={() => toggleType(t)}
              data-testid={`timeline-filter-${t}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition
                ${on ? meta.hue || "bg-neutral-200 border-neutral-300"
                     : "bg-white text-neutral-400 border-neutral-200"}`}
            >
              {meta.label || t}
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      {data.events.length === 0 ? (
        <Card className="p-6 text-center text-neutral-500" data-testid="timeline-empty">
          Geen events om te tonen.
        </Card>
      ) : (
        <div className="relative pl-6" data-testid="timeline-list">
          <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-neutral-200" />
          {data.events.map((e, i) => {
            const meta = TYPE_META[e.type] || {};
            const Icon = meta.Icon;
            const severityTone = e.severity === "error" ? "border-red-300 bg-red-50"
                                : e.severity === "warning" ? "border-amber-300 bg-amber-50/40"
                                : "border-neutral-200 bg-white";
            return (
              <div key={`${e.source_collection}-${e.source_id}-${i}`}
                   className="relative mb-4"
                   data-testid={`timeline-event-${i}`}>
                <div className="absolute -left-6 top-3 w-4 h-4 rounded-full border-2 border-white shadow flex items-center justify-center"
                     style={{ background: "#fff" }}>
                  {Icon && <Icon className="h-3 w-3 text-neutral-600" />}
                </div>
                <Card className={`p-3 border ${severityTone}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-neutral-500 tabular-nums">{fmtDate(e.datum)}</div>
                      <div className="text-sm font-semibold text-neutral-900 mt-0.5">
                        {e.title}
                      </div>
                      {e.description && (
                        <div className="text-xs text-neutral-700 mt-1 whitespace-pre-wrap">
                          {e.description}
                        </div>
                      )}
                      {e.actor && (
                        <div className="text-[10px] text-neutral-500 mt-1">door {e.actor}</div>
                      )}
                    </div>
                    <Badge variant="outline" className={`shrink-0 text-[10px] ${meta.hue}`}>
                      {meta.label || e.type}
                    </Badge>
                  </div>
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
